/**
 * Every decision the `/mitarbeiter` view makes, and every mutation it APPLIES.
 *
 * This is the view where a mistake hands someone access they should not have,
 * so it is split the way lib/startView.ts is -- and then further. Two layers
 * live here:
 *
 *  1. THE DECISIONS: which tabs exist for this role, what a PIN has to look
 *     like, which device row is still live, what a server error code says in
 *     German. Pure, exhaustively tested in Node (test/staffView.test.ts).
 *
 *  2. THE ORCHESTRATION: `*Action` functions that take their collaborators as
 *     parameters -- the API call, the confirm, the state setters, the
 *     QueryClient -- so the code that CHOOSES WHICH ROW TO ACT ON is callable
 *     from a Node test with no renderer. This is the STANDING RULE from the
 *     Task 7 and Task 8 reviews: pinning the decision is not enough, because
 *     both reviews' escapes were planted at CALL SITES, not in decision
 *     tables. `performLogout` (Boot.tsx) and `submitRequestCode`
 *     (views/Login.tsx) are the shapes copied here.
 *
 * WHAT THE ORCHESTRATION LAYER EXISTS TO PROTECT, concretely: "sperren" must
 * revoke the device the operator pointed at, a role change must write the role
 * the operator chose, and an invite must never carry a role the form did not
 * offer. Each of those is one wrong identifier away from being wrong in a way
 * no error message would ever show.
 *
 * NONE OF THIS IS AUTHORIZATION (capabilities.ts's own docblock). The API
 * refuses what this hides: /admin-api/users is owner-guarded server-side,
 * /auth/devices/* is manager-guarded, and every write is tenant-scoped by the
 * session. What this file prevents is a UI that OFFERS something the server
 * will refuse -- or worse, offers the right control pointed at the wrong row.
 */
import type { QueryClient } from '@tanstack/react-query';
import { ApiError } from './api';
import { isAllowed, type CapabilityCtx } from './capabilities';
import { t } from './i18n';
import { formatDateTime } from './startView';
import { STAFF_TABS, type StaffTab } from './router';
import { errorMessage } from '../components/ErrorNotice';
import { invalidateMutation, reportAuthFailure } from '../queries';
import type {
  AdminRole, AdminUser, DeviceRowRaw, PairingToken, RowStatus, SessionEntry, Staff, StaffRole,
} from './types';
import type { PillKind } from '../components/StatusPill';

// ── tabs ─────────────────────────────────────────────────────────────────────

/**
 * The third tab is owner-only, and this is the ONE place that says so --
 * `isAllowed('adminUsers', ctx)` is consumed, never re-derived (Task 5's whole
 * point: a second, quieter copy of the decision table is the failure mode).
 */
export function visibleStaffTabs(ctx: CapabilityCtx): StaffTab[] {
  return STAFF_TABS.filter((tab) => tab !== 'adminUsers' || isAllowed('adminUsers', ctx));
}

/**
 * A URL names a tab; the ROLE decides whether that tab exists. A manager who
 * follows `/mitarbeiter#administratoren` -- a link pasted by an owner, a
 * bookmark from before a demotion, a guess -- gets the first tab they are
 * allowed, never an owner-only panel. Fail-closed by construction: this asks
 * whether the requested tab is in the allowed list, never whether it is in a
 * list of forbidden ones (capabilities.ts's EBAY_HEALTHY_STATUSES reasoning).
 */
export function resolveStaffTab(requested: StaffTab, visible: readonly StaffTab[]): StaffTab {
  return visible.includes(requested) ? requested : (visible[0] ?? 'staff');
}

/** Literal t() calls, one per tab: see the note in components/Blocking.tsx. */
export function staffTabLabel(tab: StaffTab): string {
  switch (tab) {
    case 'staff': return t('staff.tab.staff');
    case 'devices': return t('staff.tab.devices');
    case 'adminUsers': return t('staff.tab.adminUsers');
  }
}

// ── the one snake_case mapping (plan 3c interface D19, amendment E2) ─────────

/** What the devices table renders, in this SPA's own casing. */
export interface DeviceView {
  id: string;
  label: string;
  pairedAt: string;
  lastSeenAt: string | null;
  revokedAt: string | null;
}

/**
 * api/src/db/devices.ts:84-89 selects raw D1 columns, unlike every
 * /admin-api/* route, so GET /auth/devices is the single endpoint in this
 * client that answers snake_case. It is mapped HERE, once, visibly -- not
 * normalized inside lib/api.ts, where a later server-side casing fix would
 * silently blank this table instead of failing a test, and not inside
 * `devicesQueryOptions`, which would put it back into shared infrastructure.
 *
 * views/Staff.tsx's devices panel is this function's only caller.
 */
export function mapDeviceRows(rows: readonly DeviceRowRaw[]): DeviceView[] {
  return rows.map((d) => ({
    id: d.id,
    label: d.label,
    pairedAt: d.paired_at,
    lastSeenAt: d.last_seen_at,
    revokedAt: d.revoked_at,
  }));
}

// ── display vocabulary ───────────────────────────────────────────────────────

export function staffRoleLabel(role: StaffRole): string {
  switch (role) {
    case 'scanner': return t('staff.role.scanner');
    case 'manager': return t('staff.role.manager');
  }
}

export function adminRoleLabel(role: AdminRole): string {
  switch (role) {
    case 'owner': return t('staff.users.role.owner');
    case 'manager': return t('staff.users.role.manager');
  }
}

/** Disabled is `warn`, not `error`: a deactivated account is a decision
 *  somebody made, not a fault. */
export function rowStatusPill(status: RowStatus): { kind: PillKind; label: string } {
  return status === 'active'
    ? { kind: 'ok', label: t('staff.status.active') }
    : { kind: 'warn', label: t('staff.status.disabled') };
}

/**
 * `revoked_at` is a TIMESTAMP, not a flag (api/src/db/devices.ts's header:
 * "Revocation is a timestamp -- isRevoked() checks it per request"). Any
 * non-null value means the device is locked out from its very next request,
 * so this asks for null and nothing cleverer.
 */
export function deviceStatusPill(device: DeviceView): { kind: PillKind; label: string } {
  return device.revokedAt === null
    ? { kind: 'ok', label: t('staff.devices.status.active') }
    : { kind: 'error', label: t('staff.devices.status.revoked') };
}

/** A device that has paired but never made a request has `last_seen_at` NULL
 *  (the INSERT writes NULL; `touchDeviceLastSeen` fills it later). Saying so
 *  is the point -- a blank cell reads as a rendering bug. */
export function deviceLastSeenLabel(device: DeviceView): string {
  return device.lastSeenAt === null ? t('staff.devices.lastSeenNever') : formatDateTime(device.lastSeenAt);
}

// ── PIN rules ────────────────────────────────────────────────────────────────

export type PinProblem = 'pin_required' | 'pin_length' | 'pin_digits';

/**
 * A MIRROR OF THE SERVER'S RULE, NEVER A REPLACEMENT FOR IT.
 *
 * Transcribed from api/src/adminRoutes.ts:310-326 (`pinProblem`), which is
 * itself pinned to crypto/pin.ts's `hashPin` -- `hashPin` hard-validates
 * `/^\d{4,8}$/` and THROWS past it, which is why the route's own comment calls
 * its bound "binding". The order of the three checks is copied exactly so the
 * client and the server never disagree about which complaint an input earns:
 * a non-string is `pin_required`, then length, then digits. '' is therefore
 * `pin_length`, not `pin_required`, exactly as the route answers it.
 *
 * This exists so a typo does not cost a round trip. It does NOT decide
 * anything: `staffErrorMessage` still renders `pin_length`/`pin_digits`/
 * `pin_required` when they come back from the server, because the server is
 * the authority and a client bound that drifted wider would otherwise turn a
 * 400 into an unexplained failure.
 */
export function pinProblem(pin: unknown): PinProblem | null {
  if (typeof pin !== 'string') return 'pin_required';
  if (pin.length < 4 || pin.length > 8) return 'pin_length';
  if (!/^[0-9]+$/.test(pin)) return 'pin_digits';
  return null;
}

export function pinProblemMessage(problem: PinProblem): string {
  switch (problem) {
    case 'pin_required': return t('error.pin_required');
    case 'pin_length': return t('error.pin_length');
    case 'pin_digits': return t('error.pin_digits');
  }
}

/** The server's own e-mail shape (api/src/adminRoutes.ts's POST /users) and
 *  its `normalizeEmail` (api/src/db/users.ts:10-12), mirrored for the same
 *  reason as the PIN rule: a saved round trip, never a substitute answer. */
const EMAIL_RE = /^[^@\s]+@[^@\s]+\.[^@\s]+$/;

export function normalizeEmail(email: string): string {
  return email.trim().toLowerCase();
}

// ── error copy ───────────────────────────────────────────────────────────────

/**
 * `error.<code>` (lib/api.ts:219), resolved through LITERAL t() calls rather
 * than through a computed key, so test/i18nKeys.test.ts can prove each string
 * exists -- the same reason components/Blocking.tsx's `copyFor` is a switch.
 * Anything not listed falls through to `errorMessage`, which is generic on
 * purpose: guessing at a message for an unknown code is how a UI ends up
 * asserting something the server never said.
 *
 * `name_taken` and `email_taken` carry the merged handlers' own reasoning:
 * a duplicate staff name is AMBIGUOUS, not untidy, because the scanner's
 * name+PIN login resolves by name (adminRoutes.ts:349-352); and the e-mail
 * uniqueness index is GLOBAL, so the clash may be in another tenant and the
 * copy must not hint at one -- that would be an enumeration oracle
 * (adminRoutes.ts:446-449).
 */
export function staffErrorMessage(error: unknown): string {
  const code = error instanceof ApiError ? error.code : null;
  switch (code) {
    case 'display_name_required': return t('error.display_name_required');
    case 'pin_required': return t('error.pin_required');
    case 'pin_length': return t('error.pin_length');
    case 'pin_digits': return t('error.pin_digits');
    case 'name_taken': return t('error.name_taken');
    case 'email_taken': return t('error.email_taken');
    case 'invalid_email': return t('error.invalid_email');
    case 'invalid_label': return t('error.invalid_label');
    case 'cannot_disable_self': return t('error.cannot_disable_self');
    case 'not_found': return t('error.not_found');
    case 'forbidden': return t('error.forbidden');
    default: return errorMessage(error);
  }
}

// ── pairing ──────────────────────────────────────────────────────────────────

/**
 * The countdown next to the QR. `PAIRING_CODE_TTL_MS` is ten minutes
 * (api/src/db/devices.ts:14) and `claimPairingCode` compares `expires_at > now`
 * server-side, so a code that has run out is refused rather than tolerated --
 * the panel has to say so, or the operator blames the camera.
 *
 * Floored at zero and reported as expired at exactly 0: a negative countdown
 * would render "-00:03" and read as a rendering bug rather than as the answer.
 */
export function pairingCountdown(expiresAt: string, nowMs: number): { expired: boolean; label: string } {
  const remainingMs = Date.parse(expiresAt) - nowMs;
  // An unparseable expiry (NaN) must read as expired, not as "plenty of time":
  // adminFetch casts the body with no runtime validation, so a malformed
  // timestamp is reachable, and the pessimistic answer is the safe one.
  if (!(remainingMs > 0)) return { expired: true, label: t('staff.pair.expired') };
  const totalSeconds = Math.floor(remainingMs / 1000);
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  return {
    expired: false,
    label: t('staff.pair.expiresIn', { time: `${minutes}:${String(seconds).padStart(2, '0')}` }),
  };
}

// ── sessions ─────────────────────────────────────────────────────────────────

/**
 * GET /auth/sessions returns EVERY row for the subject -- `listSessionsForSubject`
 * filters on nothing but subject and orders by created_at (api/src/db/sessions.ts:56-64)
 * -- so revoked and long-expired rows arrive too. Listing those under "Ihre
 * Sitzungen" with a "Beenden" button would offer an action whose only possible
 * answer is a 404, and would make a tidy list look like a compromised account.
 */
export function activeSessions(rows: readonly SessionEntry[], nowMs: number): SessionEntry[] {
  return rows.filter((s) => s.revokedAt === null && Date.parse(s.expiresAt) > nowMs);
}

export function sessionLine(session: SessionEntry): { createdAt: string; expiresAt: string } {
  return { createdAt: formatDateTime(session.createdAt), expiresAt: formatDateTime(session.expiresAt) };
}

// ── orchestration ────────────────────────────────────────────────────────────

/** The three collaborators every mutation on this view needs. Passed in, never
 *  reached for, so a Node test can hold all three. */
export interface MutationShell {
  queryClient: QueryClient;
  setBusy: (busy: boolean) => void;
  setError: (message: string | null) => void;
}

/**
 * One mutation, applied: busy on, previous error cleared, run, invalidate
 * THROUGH THE SHARED MAP, report.
 *
 * `invalidateMutation` runs only on success -- a failed write changed nothing
 * server-side, and refetching four lists to prove that is noise. It reads
 * queries.ts's `INVALIDATES` rather than naming keys here, so "revoking a
 * device refreshes the device list" is a row a reviewer can see rather than
 * an argument at a call site (queries.ts rule 2, and the controller's
 * instruction that these must refresh through the map).
 *
 * Returns whether the write succeeded, so a call site can close a dialog or
 * clear a form -- that is plumbing, not a decision this function owns.
 */
export async function runStaffMutation<R>(
  mutation: string,
  run: () => Promise<R>,
  shell: MutationShell,
  onSuccess?: (result: R) => void,
): Promise<boolean> {
  shell.setBusy(true);
  shell.setError(null);
  try {
    const result = await run();
    invalidateMutation(shell.queryClient, mutation);
    onSuccess?.(result);
    return true;
  } catch (error) {
    // A 401 drives the SESSION, not just this panel's error line (final review
    // I-1). Here rather than at each call site for the reason the whole file is
    // shaped this way: a per-panel copy is a per-panel thing to forget.
    reportAuthFailure(shell.queryClient, error);
    shell.setError(staffErrorMessage(error));
    return false;
  } finally {
    // Always, success or failure: a form left permanently disabled after one
    // failed write is indistinguishable from a hung app.
    shell.setBusy(false);
  }
}

// staff ----------------------------------------------------------------------

export interface CreateStaffIO extends MutationShell {
  createStaff: (json: { displayName: string; pin: string; role: StaffRole }) => Promise<unknown>;
  onCreated: () => void;
}

export interface StaffDraft {
  displayName: string;
  pin: string;
  role: StaffRole;
}

export async function createStaffAction(draft: StaffDraft, io: CreateStaffIO): Promise<boolean> {
  const displayName = draft.displayName.trim();
  if (!displayName) {
    io.setError(t('error.display_name_required'));
    return false;
  }
  const problem = pinProblem(draft.pin);
  if (problem) {
    io.setError(pinProblemMessage(problem));
    return false;
  }
  return runStaffMutation(
    'createStaff',
    () => io.createStaff({ displayName, pin: draft.pin, role: draft.role }),
    io,
    () => io.onCreated(),
  );
}

export interface UpdateStaffIO extends MutationShell {
  updateStaff: (id: string, json: { role?: StaffRole; status?: RowStatus; pin?: string }) => Promise<unknown>;
}

/**
 * THE ROLE THE OPERATOR CHOSE, ON THE ROW THE OPERATOR CHOSE. Both halves are
 * arguments, and both are asserted in Node: a call site that passes a
 * neighbouring row, or that re-derives the role from anything but the select's
 * value, is a promotion nobody would notice -- `manager` is what the scanner
 * checks for its own privileged actions.
 */
export async function changeStaffRoleAction(staff: Staff, role: StaffRole, io: UpdateStaffIO): Promise<boolean> {
  // A PATCH with the role it already has is a no-op that still writes an
  // audit row (adminRoutes.ts logs `staff.update` for any non-empty change
  // set), which makes the audit trail lie about what an operator did.
  if (role === staff.role) return false;
  return runStaffMutation('updateStaff', () => io.updateStaff(staff.id, { role }), io);
}

export async function toggleStaffStatusAction(staff: Staff, io: UpdateStaffIO): Promise<boolean> {
  const status: RowStatus = staff.status === 'active' ? 'disabled' : 'active';
  return runStaffMutation('updateStaff', () => io.updateStaff(staff.id, { status }), io);
}

export interface ResetStaffPinIO extends UpdateStaffIO {
  /** Called with the PIN THE MANAGER TYPED, so the panel can show it once. */
  onReset: (pin: string) => void;
}

/**
 * THE PIN IS NEVER GENERATED HERE, NEVER LOGGED AND NEVER PERSISTED. The
 * manager types it, it travels in one PATCH body, it is handed back to the
 * caller exactly once so the panel can show it while the manager reads it out,
 * and it lives nowhere else -- no console, no query cache, no storage (the
 * ESLint ban on localStorage/sessionStorage is the mechanical half of that).
 * The server's own audit trail makes the same promise from the other side:
 * `staff.pin_reset` is logged as a separate action verb with NO meta,
 * precisely so the PIN is not in it (adminRoutes.ts).
 */
export async function resetStaffPinAction(staff: Staff, pin: string, io: ResetStaffPinIO): Promise<boolean> {
  const problem = pinProblem(pin);
  if (problem) {
    io.setError(pinProblemMessage(problem));
    return false;
  }
  return runStaffMutation('updateStaff', () => io.updateStaff(staff.id, { pin }), io, () => io.onReset(pin));
}

// devices --------------------------------------------------------------------

export interface RevokeDeviceIO extends MutationShell {
  revokeDevice: (deviceId: string) => Promise<unknown>;
  /** window.confirm at the call site; a function here so the confirmation TEXT
   *  -- which is the only thing naming which device is about to die -- is
   *  assertable without a browser. */
  confirm: (message: string) => boolean;
}

/**
 * Locking a device is immediate and one-way (a new pairing is the only way
 * back), so the confirmation names the LABEL -- and the id sent is that same
 * device's. The Node test asserts both against the SAME row, which is what
 * makes "confirms about device A, revokes device B" a failure rather than a
 * detail: the confirmation is the operator's last chance to notice.
 */
export async function revokeDeviceAction(device: DeviceView, io: RevokeDeviceIO): Promise<boolean> {
  if (!io.confirm(t('staff.devices.confirmRevoke', { label: device.label }))) return false;
  return runStaffMutation('revokeDevice', () => io.revokeDevice(device.id), io);
}

export interface PairDeviceIO extends MutationShell {
  pairToken: (label: string) => Promise<PairingToken>;
  onToken: (token: PairingToken) => void;
}

/** The label bound is the server's own (`!label || label.length > 64` after a
 *  trim, api/src/authRoutes.ts) -- mirrored, not invented. */
export async function issuePairingTokenAction(label: string, io: PairDeviceIO): Promise<boolean> {
  const trimmed = label.trim();
  if (!trimmed || trimmed.length > 64) {
    io.setError(t('error.invalid_label'));
    return false;
  }
  return runStaffMutation('pairToken', () => io.pairToken(trimmed), io, (token) => io.onToken(token));
}

// admin users ----------------------------------------------------------------

export interface InviteUserIO extends MutationShell {
  inviteUser: (json: { email: string; role: 'manager' }) => Promise<unknown>;
  onInvited: () => void;
}

/**
 * `role: 'manager'` IS A CONSTANT OF THIS FUNCTION, not a parameter and not a
 * form field. v1 creates managers only and the merged handler 400s anything
 * else, but the reason is the part worth keeping in the client: owner carries
 * subscription, billing and deletion (admin spec 4.6), and handing that out
 * from a self-serve form has no undo (adminRoutes.ts:437-439). A second owner
 * is a support operation. There is nothing here for a call site to get wrong.
 */
export async function inviteUserAction(email: string, io: InviteUserIO): Promise<boolean> {
  const normalized = normalizeEmail(email);
  if (!EMAIL_RE.test(normalized)) {
    io.setError(t('error.invalid_email'));
    return false;
  }
  return runStaffMutation(
    'inviteUser',
    () => io.inviteUser({ email: normalized, role: 'manager' }),
    io,
    () => io.onInvited(),
  );
}

export interface UpdateUserIO extends MutationShell {
  updateUser: (id: string, json: { status: RowStatus }) => Promise<unknown>;
}

/**
 * Locking yourself out of the only owner account has no in-product remedy --
 * it would need plan 2b's emergency-session webhook and an operator
 * (adminRoutes.ts's own comment on the 400). The server answers
 * `cannot_disable_self`; this is the same answer rendered BEFORE the click, as
 * a disabled button with a hint, because a request that can only fail is not a
 * control.
 */
export function isSelf(user: AdminUser, currentUserId: string): boolean {
  return user.id === currentUserId;
}

export async function toggleUserStatusAction(
  user: AdminUser,
  currentUserId: string,
  io: UpdateUserIO,
): Promise<boolean> {
  if (isSelf(user, currentUserId)) {
    // Belt and braces with the disabled button above: a call site that forgets
    // the guard must still not send a request whose only answer is a 400.
    io.setError(t('error.cannot_disable_self'));
    return false;
  }
  const status: RowStatus = user.status === 'active' ? 'disabled' : 'active';
  return runStaffMutation('updateUser', () => io.updateUser(user.id, { status }), io);
}

// sessions -------------------------------------------------------------------

export interface RevokeSessionIO extends MutationShell {
  revokeSession: (sessionId: string) => Promise<unknown>;
  confirm: (message: string) => boolean;
}

/**
 * OWN SESSIONS ONLY. POST /auth/sessions/revoke refuses any session whose
 * subject is not the caller (api/src/authRoutes.ts), and GET /auth/sessions
 * only ever returns the caller's -- so the copy says "Ihre Sitzungen" and
 * never "alle Sitzungen", which would promise a phase-3 owner feature that
 * does not exist.
 *
 * The CURRENT session is deliberately not revocable from this list: the server
 * would accept it, and the result is a shell that still looks signed in while
 * every subsequent request 401s. "Abmelden" in the header is that action, and
 * it also clears the cache and reloads (Boot.tsx's `performLogout`).
 */
export async function revokeSessionAction(session: SessionEntry, io: RevokeSessionIO): Promise<boolean> {
  if (session.current) {
    io.setError(t('staff.sessions.currentHint'));
    return false;
  }
  if (!io.confirm(t('staff.sessions.confirmRevoke'))) return false;
  return runStaffMutation('revokeSession', () => io.revokeSession(session.id), io);
}
